import React, { useEffect, useState } from "react";
import { FaCalendarCheck, FaUser, FaTimes } from "react-icons/fa";
import { getSubs, getSubUser, updateSubscriptionStatus } from "../../utils/sub";
import { useParams } from "react-router-dom";

const statusOptions = ["All", "Active", "Pending", "Expired", "Cancelled"];

const SubscriptionPage = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const { userId } = useParams();
    useEffect(() => {
        const loadSubscriptions = async () => {
            try {
                const data = await getSubUser(userId); // Pass token to API if needed
                setSubscriptions(data?.subscriptions || []); // Ensure subscriptions is an array
                console.log(data);
            } catch (err) {
                setError("Failed to fetch subscriptions. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        loadSubscriptions();
    }, [userId]); // Ensure the effect is triggered when userId or token changes

    // Ensure subscriptions is an array before calling .filter()
    const filteredSubscriptions = (subscriptions || []).filter(
        (sub) =>
            (filterStatus === "All" || sub?.status === filterStatus) &&
            (sub?.subscription_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                sub?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
    const displayedSubscriptions = filteredSubscriptions.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );



    const handleRowClick = (subscription) => {
        setSelectedSubscription(subscription);
    };

    const handleStatusChange = async () => {
        if (selectedSubscription) {
            try {
                await updateSubscriptionStatus(selectedSubscription?.subscription_id, selectedSubscription.status);

                setSubscriptions((prevSubs) =>
                    prevSubs.map((sub) =>
                        sub.subscription_id === selectedSubscription?.subscription_id ? { ...sub, status: selectedSubscription.status } : sub
                    )
                );

                setSelectedSubscription(null);
            } catch (error) {
                console.error("Failed to update subscription status", error);
            }
        }
    };

    const PaginationButton = ({ direction, disabled, onClick }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`px-3 py-1 rounded ${disabled ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"}`}
            aria-label={`Go to ${direction} page`}
        >
            {direction === "prev" ? "<" : ">"}
        </button>
    );

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "next" && prev < totalPages) return prev + 1;
            if (direction === "prev" && prev > 1) return prev - 1;
            return prev;
        });
    };

    {
        return (
            <div>
                <h1 className="text-3xl font-bold text-blue-600 mb-6 text-center">User Subscriptions</h1>

                <div className="flex justify-between items-center mb-4">
                    <input
                        type="text"
                        placeholder="Search by ID or User..."
                        className="border border-gray-300 px-4 py-2 rounded-md w-1/3"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="border px-3 py-2 rounded-md"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        {statusOptions.map((status) => (
                            <option key={status} value={status}>
                                {status}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="p-6 bg-white shadow-lg rounded-lg">
                    <table className="table-auto w-full border-collapse border border-gray-200">
                        <thead className="bg-blue-100">
                            <tr>
                                <th className="border px-4 py-2 text-left text-blue-800 font-semibold">Subscription ID</th>
                                <th className="border px-4 py-2 text-left text-blue-800 font-semibold">User</th>
                                <th className="border px-4 py-2 text-left text-blue-800 font-semibold">Status</th>
                                <th className="border px-4 py-2 text-left text-blue-800 font-semibold">Expiry Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedSubscriptions?.map((subscription) => (
                                <tr
                                    key={subscription.subscription_id}
                                    className="hover:bg-blue-50 cursor-pointer"
                                    onClick={() => handleRowClick(subscription)}
                                >
                                    <td className="border px-4 py-2 text-gray-700">{subscription.subscription_id}</td>
                                    <td className="border px-4 py-2 text-gray-700">{subscription.fullName}</td>
                                    <td className="border px-4 py-2 text-gray-700">{subscription.status}</td>
                                    <td className="border px-4 py-2 text-gray-700">{new Date(subscription.expiry_date).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-5">
                    <PaginationButton
                        direction="prev"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange("prev")}
                    />
                    <span className="text-gray-700 font-medium">
                        Page {currentPage} of {totalPages}
                    </span>
                    <PaginationButton
                        direction="next"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange("next")}
                    />
                </div>

                {selectedSubscription && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
                        <div className="bg-white p-6 rounded-lg shadow-xl w-96 relative">
                            <button
                                onClick={() => setSelectedSubscription(null)}
                                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
                            >
                                <FaTimes />
                            </button>
                            <h2 className="text-xl font-semibold mb-4 text-center text-blue-600">Subscription Details</h2>
                            <img
                                src={selectedSubscription.image}
                                alt="Subscription Proof"
                                className="my-4 mx-auto max-w-full h-auto rounded-md shadow"
                            />
                            <div className="text-gray-700 space-y-2">
                                <p><strong>Reference Id:</strong> {selectedSubscription.reference_number}</p>
                            </div>

                            {/* Status Dropdown */}
                            <div className="mt-4">
                                <label className="block text-gray-700 mb-1 font-semibold">Update Status:</label>
                                <select
                                    value={selectedSubscription.status}
                                    onChange={(e) =>
                                        setSelectedSubscription((prev) => ({ ...prev, status: e.target.value }))
                                    }
                                    className="border border-gray-300 px-3 py-2 rounded-md w-full"
                                >
                                    {statusOptions
                                        .filter((status) => status !== "All") // Exclude "All" from selection
                                        .map((status) => (
                                            <option key={status} value={status}>
                                                {status}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            {/* Update Button */}
                            <button
                                onClick={handleStatusChange}
                                className="mt-4 w-full px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-300 shadow-md flex items-center justify-center"
                            >
                                <FaCalendarCheck className="mr-2" /> Update Status
                            </button>
                        </div>
                    </div>
                )}

            </div>
        );
    }
}


export default SubscriptionPage;
